import React, { Suspense, useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import routes from "./routes";
import {
  Container,
  Box,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import { DRAWER_WIDTH } from "./constants/layout";

const drawerWidth = DRAWER_WIDTH;

function dashboardFallbackPath(role) {
  switch (role) {
    case "super_admin":
      return "/dashboard/super-admin";
    case "admin":
      return "/dashboard/admin";
    case "standard":
      return "/dashboard/user";
    case "executive":
      return "/dashboard/executive";
    case "department_head":
      return "/dashboard/department-head";
    default:
      return "/dashboard/user";
  }
}

function ProtectedRoute({ allowedRoles, role, children }) {
  if (allowedRoles?.length) {
    if (!role) return null;
    if (!allowedRoles.includes(role)) {
      return <Navigate to={dashboardFallbackPath(role)} replace />;
    }
  }
  return children;
}

function AppContent() {
  const [selectedItem, setSelectedItem] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [authLoading, setAuthLoading] = useState(true);

  const publicRoutes = routes.filter((route) =>
    ["Login", "Register", "Password Recovery", "Reset Password"].includes(
      route.name,
    ),
  );
  const protectedRoutes = routes.filter(
    (route) => !publicRoutes.some((pub) => pub.name === route.name),
  );

  // useEffect(() => {
  //   const storedAuth = localStorage.getItem("token");
  //   const storedRole = localStorage.getItem("role");
  //   const storedDepartmentId = localStorage.getItem("department_id");
  //   const currentPath = location.pathname;
  //   console.log(">>>>>>>>>>22", role);

  //   // Define path-to-item mapping
  //   const pathToItemKey = {
  //     "/dashboard/admin": "admin-dashboard",
  //     "/dashboard/super-admin": "super-admin-dashboard",
  //     "/dashboard/user": "user-dashboard",
  //     "/dashboard/executive": "executive-dashboard",
  //     "/dashboard/department-head": "department-head-dashboard",
  //     "/dashboard/user-management": "user-management",
  //     "/dashboard/reports": "reports",
  //     "/dashboard/analytics": "analytics",
  //     "/dashboard/tickets": "tickets",
  //     "/dashboard/locations": "locations",
  //     "/dashboard/outlet": "outlet",
  //     "/dashboard/sla-management": "sla-management",
  //     "/dashboard/department": "department",
  //     "/dashboard/profile": "profile",
  //     "/dashboard/notifications": "notifications",
  //     "/dashboard/department-ticket": "department-ticket",
  //     "/dashboard/assigned-tickets": "assigned-tickets",
  //   };

  //   if (storedAuth) {
  //     setIsAuthenticated(true);
  //     setRole(storedRole);
  //     setDepartmentId(storedDepartmentId);

  //     // Set selectedItem based on current path or default to role-based dashboard
  //     const itemKey =
  //       pathToItemKey[currentPath] ||
  //       (storedRole === "super_admin"
  //         ? "super-admin-dashboard"
  //         : storedRole
  //           ? `${storedRole === "standard" ? "user" : storedRole}-dashboard`
  //           : "dashboard");
  //     setSelectedItem(itemKey);

  //     const matchedRoute = protectedRoutes.find((r) => r.path === currentPath);
  //     if (
  //       currentPath === "/dashboard" || !matchedRoute ||
  //       (matchedRoute.allowedRoles?.length && !matchedRoute.allowedRoles.includes(storedRole))
  //     ) {
  //       const defaultRoute = dashboardFallbackPath(storedRole);
  //       if (currentPath !== defaultRoute) {
  //         navigate(defaultRoute, { replace: true });
  //       }
  //     }
  //   } else {
  //     // Redirect to login if not authenticated and not on a public route
  //     if (!publicRoutes.some((route) => route.path === currentPath)) {
  //       navigate("/", { replace: true });
  //     }
  //   }
  // }, [location.pathname, navigate, protectedRoutes]);

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedRole = localStorage.getItem("role");
    const storedDepartmentId = localStorage.getItem("department_id");

    if (storedToken) {
      setIsAuthenticated(true);
      setRole(storedRole);
      setDepartmentId(storedDepartmentId);

      const currentPath = location.pathname;

      const pathToItemKey = {
        "/dashboard/admin": "admin-dashboard",
        "/dashboard/super-admin": "super-admin-dashboard",
        "/dashboard/user": "user-dashboard",
        "/dashboard/executive": "executive-dashboard",
        "/dashboard/department-head": "department-head-dashboard",
        "/dashboard/user-management": "user-management",
        "/dashboard/reports": "reports",
        "/dashboard/analytics": "analytics",
        "/dashboard/tickets": "tickets",
        "/dashboard/locations": "locations",
        "/dashboard/outlet": "outlet",
        "/dashboard/sla-management": "sla-management",
        "/dashboard/department": "department",
        "/dashboard/profile": "profile",
        "/dashboard/change-password": "change-password",
        "/dashboard/notifications": "notifications",
        "/dashboard/department-ticket": "department-ticket",
        "/dashboard/assigned-tickets": "assigned-tickets",
      };

      setSelectedItem(
        pathToItemKey[currentPath] ||
          (storedRole === "super_admin"
            ? "super-admin-dashboard"
            : storedRole
              ? `${storedRole === "standard" ? "user" : storedRole}-dashboard`
              : "dashboard"),
      );
    }

    setAuthLoading(false);
  }, []);

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    let path;

    switch (item) {
      case "admin-dashboard":
        path = "/dashboard/admin";
        break;
      case "super-admin-dashboard":
        path = "/dashboard/super-admin";
        break;
      case "user-dashboard":
        path = "/dashboard/user";
        break;
      case "executive-dashboard":
        path = "/dashboard/executive";
        break;
      case "department-head-dashboard":
        path = "/dashboard/department-head";
        break;
      case "user-management":
        path = "/dashboard/user-management";
        break;
      case "reports":
        path = "/dashboard/reports";
        break;
      case "profile":
        path = "/dashboard/profile";
        break;
      case "change-password":
        path = "/dashboard/change-password";
        break;
      case "analytics":
        path = "/dashboard/analytics";
        break;
      case "tickets":
        path = "/dashboard/tickets";
        break;
      case "notifications":
        path = "/dashboard/notifications";
        break;
      case "locations":
        path = "/dashboard/locations";
        break;
      case "outlet":
        path = "/dashboard/outlet";
        break;
      case "sla-management":
        path = "/dashboard/sla-management";
        break;
      case "department":
        path = "/dashboard/department";
        break;
      case "department-ticket":
        path = "/dashboard/department-ticket";
        break;
      case "assigned-tickets":
        path = "/dashboard/assigned-tickets";
        break;
      default:
        path = role
          ? `/dashboard/${role === "standard" ? "user" : role}`
          : "/dashboard/user";
    }
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleMenuToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
    const storedRole = localStorage.getItem("role");
    const storedDepartmentId = localStorage.getItem("department_id");
    setRole(storedRole);
    setDepartmentId(storedDepartmentId);
    localStorage.setItem("isAuthenticated", "true");

    const rolePath = dashboardFallbackPath(storedRole);
    setSelectedItem(
      storedRole === "super_admin"
        ? "super-admin-dashboard"
        : storedRole
          ? `${storedRole === "standard" ? "user" : storedRole}-dashboard`
          : "dashboard",
    );
    navigate(rolePath);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setRole(null);
    setDepartmentId(null);
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("department_id");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("rememberedEmail");
    localStorage.removeItem("rememberedPassword");
    setSelectedItem("");
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: "Successfully logged out!",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    }).then(() => {
      navigate("/");
    });
  };

  if (authLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {isAuthenticated && (
        <Box sx={{ display: "flex", flex: 1, width: "100%" }}>
          <Sidebar
            selectedItem={selectedItem}
            onItemSelect={handleItemSelect}
            open={mobileOpen}
            onClose={handleDrawerClose}
            role={role}
            onLogout={handleLogout}
          />
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              width: { xs: "100%", sm: `calc(100% - ${drawerWidth}px)` },
              maxWidth: "100%",
            }}
          >
            <Header
              onMenuToggle={handleMenuToggle}
              onLogout={handleLogout}
              role={role}
              departmentId={departmentId}
            />
            <Box
              sx={{
                p: { xs: 1, sm: 2 },
                mt: { xs: 7, sm: 8 },
                flex: 1,
                overflowY: "auto",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <Container
                maxWidth={false}
                sx={{ px: { xs: 1, sm: 2 }, maxWidth: "100%" }}
              >
                <Suspense
                  fallback={
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  }
                >
                  <Routes>
                    {protectedRoutes.map((route, index) => (
                      <Route
                        key={index}
                        path={route.path}
                        element={
                          <ProtectedRoute
                            allowedRoles={route.allowedRoles}
                            role={role}
                          >
                            <route.component />
                          </ProtectedRoute>
                        }
                      />
                    ))}
                    <Route
                      path="/dashboard"
                      element={
                        role ? (
                          <Navigate to={dashboardFallbackPath(role)} replace />
                        ) : (
                          <Navigate to="/" replace />
                        )
                      }
                    />
                    <Route
                      path="*"
                      element={
                        <Navigate to={dashboardFallbackPath(role)} replace />
                      }
                    />
                  </Routes>
                </Suspense>
              </Container>
            </Box>
          </Box>
        </Box>
      )}
      {!isAuthenticated && (
        <Suspense
          fallback={
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          }
        >
          <Routes>
            {publicRoutes.map((route, index) => (
              <Route
                key={index}
                path={route.path}
                element={
                  route.name === "Login" ? (
                    <route.component onLogin={handleLogin} />
                  ) : (
                    <route.component />
                  )
                }
                exact={route.exact}
              />
            ))}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      )}
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
